import Papa from 'papaparse';
import { getSheetRows, getSpecialEventsSheetIds, isSheetsMode, SHEET_NAMES } from '@/app/shared/sheets';
import type {
  SpecialEvent,
  SpecialEventDetails,
  SpecialEventInventory,
  SpecialEventsAnalysisDashboardData,
  SpecialEventsFactorDistributionItem,
  SpecialEventsLocationAnalysisItem,
  SpecialEventsAssortmentGroupAnalysisItem,
  SpecialEventsHighFactorItem,
  SpecialEventsLowFactorItem,
  SpecialEventsSkuLocationLevelItem,
} from '@/app/modules/special-events/interfaces';
const eventsCsvModules = import.meta.glob('/src/mock-data/special-events-events.csv', {
  query: '?raw',
  import: 'default',
  eager: true,
});
const eventsCsv = (Object.values(eventsCsvModules)[0] as string | undefined) ?? '';
const skuLocationsCsvModules = import.meta.glob('/src/mock-data/special-events-sku-locations.csv', {
  query: '?raw',
  import: 'default',
  eager: true,
});
const skuLocationsCsv = (Object.values(skuLocationsCsvModules)[0] as string | undefined) ?? '';
type EventRow = {
  id: string;
  event: string;
  eventType: string;
  currentInventory: number | null;
  estimatedInventory: number | null;
  startDate: string | null;
  endDate: string | null;
  preparationDate: string | null;
  avgFactor: number | null;
  avgSalesRate: number | null;
  segmentation: string | null;
  lastModify: string | null;
  createdAt: string | null;
  pctLocations: number | null;
  pctSkus: number | null;
  pctSkuLocations: number | null;
};
type SkuLocationRow = {
  eventName: string;
  locationName: string | null;
  locationId: string | null;
  skuName: string | null;
  skuId: string | null;
  productName: string | null;
  productId: string | null;
  factor: number | null;
  percentageChange: number | null;
  atSite: number | null;
  optimalStock: number | null;
  minStock: number | null;
  maxStock: number | null;
  segment: string | null;
  preparationDate: string | null;
  nextReplenishment: string | null;
  saleRate: number | null;
  categories: string | null;
  styles: string | null;
  kind: string | null;
  assortmentGroup: string | null;
  department: string | null;
  brand: string | null;
  season: string | null;
  atWh: number | null;
};
function str(value: unknown): string {
  if (value == null) return '';
  return String(value).trim();
}
function num(value: unknown): number | null {
  if (value == null) return null;
  const s = String(value).trim().replace(/[,%$]/g, '');
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}
function pick(row: Record<string, unknown>, ...keys: string[]): unknown {
  for (const k of keys) {
    if (row[k] != null && String(row[k]).trim() !== '') return row[k];
  }
  return null;
}
function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
function eventNameKey(value: string | null | undefined): string {
  return slugify(str(value).replace(/['’`]/g, ''));
}
function matchesEventName(left: string | null | undefined, right: string | null | undefined): boolean {
  const leftKey = eventNameKey(left);
  const rightKey = eventNameKey(right);
  if (!leftKey || !rightKey) {
    return false;
  }
  return leftKey === rightKey || leftKey.includes(rightKey) || rightKey.includes(leftKey);
}
function filterSkuRowsByEvent<T extends { eventName: string | null }>(
  rows: T[],
  eventName: string | null | undefined,
): T[] {
  if (!eventName) {
    return rows;
  }
  const rowsWithEventName = rows.filter((row) => eventNameKey(row.eventName));
  if (!rowsWithEventName.length) {
    return rows;
  }
  return rowsWithEventName.filter((row) => matchesEventName(row.eventName, eventName));
}
function normalizeEventRow(raw: Record<string, unknown>, index: number): EventRow {
  return {
    id: `se-${index + 1}`,
    event: str(pick(raw, 'Event', 'event', 'Name', 'name')),
    eventType: str(pick(raw, 'Event type', 'event_type', 'eventType', 'Type', 'type')),
    currentInventory: num(pick(raw, 'Current Inventory', 'current_inventory', 'currentInventory')),
    estimatedInventory: num(
      pick(raw, 'Estimated Inventory', 'estimated_inventory', 'estimatedInventory'),
    ),
    startDate: str(pick(raw, 'Start date', 'start_date', 'startDate')),
    endDate: str(pick(raw, 'End date', 'end_date', 'endDate')),
    preparationDate:
      str(pick(raw, 'Preparation date', 'preparation_date', 'preparationDate')) || null,
    avgFactor: num(pick(raw, 'Avg factor', 'avg_factor', 'avgFactor', 'Average Factor')),
    avgSalesRate: num(
      pick(raw, 'Avg Sales rate', 'avg_sales_rate', 'avgSalesRate', 'Avg Sales Rate'),
    ),
    segmentation: str(pick(raw, 'Segmentation', 'segmentation', 'Segment', 'segment')) || null,
    lastModify:
      str(pick(raw, 'Last modify', 'last_modify', 'lastModify', 'Updated at', 'updated_at')) ||
      null,
    createdAt: str(pick(raw, 'Created at', 'created_at', 'createdAt')) || null,
    pctLocations: num(pick(raw, '% Locations', '% Locations in the event', 'pctLocations')),
    pctSkus: num(pick(raw, '% SKUs', '% SKUs in the event', 'pctSkus')),
    pctSkuLocations: num(
      pick(raw, '% SKU-Locations', '% SKU-Locations in the event', 'pctSkuLocations'),
    ),
  };
}
function normalizeSkuLocationRow(raw: Record<string, unknown>): SkuLocationRow {
  return {
    eventName: str(pick(raw, 'Event', 'event', 'Event Name', 'event_name')),
    locationName: str(pick(raw, 'Location Name', 'location_name', 'locationName')) || null,
    locationId: str(pick(raw, 'Location ID', 'location_id', 'locationId')) || null,
    skuName: str(pick(raw, 'SKU Name', 'sku_name', 'skuName')) || null,
    skuId: str(pick(raw, 'SKU ID', 'sku_id', 'skuId')) || null,
    productName: str(pick(raw, 'Product Name', 'product_name', 'productName')) || null,
    productId: str(pick(raw, 'Product ID', 'product_id', 'productId')) || null,
    factor: num(pick(raw, 'Factor', 'factor')),
    percentageChange: num(pick(raw, 'Percentage Change', 'percentage_change', 'percentageChange')),
    atSite: num(pick(raw, 'At Site', 'at_site', 'atSite')),
    optimalStock: num(pick(raw, 'Optimal Stock', 'optimal_stock', 'optimalStock')),
    minStock: num(pick(raw, 'Min Stock', 'min_stock', 'minStock')),
    maxStock: num(pick(raw, 'Max Stock', 'max_stock', 'maxStock')),
    segment: str(pick(raw, 'Segment', 'segment')) || null,
    preparationDate:
      str(pick(raw, 'Preparation Date', 'preparation_date', 'preparationDate')) || null,
    nextReplenishment:
      str(pick(raw, 'Next Replenishment', 'next_replenishment', 'nextReplenishment')) || null,
    saleRate: num(pick(raw, 'Sale Rate', 'sale_rate', 'saleRate', 'Sales Rate', 'sales_rate')),
    categories: str(pick(raw, 'Categories', 'categories', 'Category', 'category')) || null,
    styles: str(pick(raw, 'Styles', 'styles', 'Style', 'style')) || null,
    kind: str(pick(raw, 'Kind', 'kind')) || null,
    assortmentGroup:
      str(pick(raw, 'Assortment Group', 'assortment_group', 'assortmentGroup')) || null,
    department: str(pick(raw, 'Department', 'department')) || null,
    brand: str(pick(raw, 'Brand', 'brand')) || null,
    season: str(pick(raw, 'Season', 'season')) || null,
    atWh: num(pick(raw, 'At WH', 'at_wh', 'atWh')),
  };
}
let eventRowsCache: EventRow[] | null = null;
let skuLocationRowsCache: SkuLocationRow[] | null = null;
export function resetSpecialEventsFlatCache() {
  eventRowsCache = null;
  skuLocationRowsCache = null;
}
async function loadEventRows(): Promise<EventRow[]> {
  if (eventRowsCache) return eventRowsCache;
  if (isSheetsMode('special-events')) {
    const { eventsId } = getSpecialEventsSheetIds();
    const rows = await getSheetRows<Record<string, unknown>>(
      SHEET_NAMES.specialEventsEvents,
      'special-events',
      eventsId,
    );
    if (rows.length) {
      eventRowsCache = rows.map((row, i) => normalizeEventRow(row, i));
      return eventRowsCache;
    }
  }
  if (!eventsCsv.trim()) {
    eventRowsCache = [];
    return eventRowsCache;
  }
  const parsed = Papa.parse<Record<string, unknown>>(eventsCsv, {
    header: true,
    skipEmptyLines: true,
  });
  eventRowsCache = parsed.data.map((row, i) => normalizeEventRow(row, i));
  return eventRowsCache;
}
async function loadSkuLocationRows(): Promise<SkuLocationRow[]> {
  if (skuLocationRowsCache) return skuLocationRowsCache;
  if (isSheetsMode('special-events')) {
    const { skuDetailsId } = getSpecialEventsSheetIds();
    const rows = await getSheetRows<Record<string, unknown>>(
      SHEET_NAMES.specialEventsSkuLocations,
      'special-events',
      skuDetailsId,
    );
    if (rows.length) {
      skuLocationRowsCache = rows.map(normalizeSkuLocationRow);
      return skuLocationRowsCache;
    }
  }
  if (!skuLocationsCsv.trim()) {
    skuLocationRowsCache = [];
    return skuLocationRowsCache;
  }
  const parsed = Papa.parse<Record<string, unknown>>(skuLocationsCsv, {
    header: true,
    skipEmptyLines: true,
  });
  skuLocationRowsCache = parsed.data.map(normalizeSkuLocationRow);
  return skuLocationRowsCache;
}
export async function getSpecialEventsFromFlat(): Promise<SpecialEvent[]> {
  const rows = await loadEventRows();
  return rows.map((row) => ({
    id: row.id,
    event_id: row.id,
    external_id: slugify(row.event),
    name: row.event,
    description: null,
    type: row.eventType,
    kind: row.eventType,
    discount: '',
    segment: row.segmentation ?? '',
    stage: 'approved' as const,
    is_approved: true,
    avg_factor: row.avgFactor,
    min_factor: null,
    max_factor: null,
    factor: row.avgFactor,
    percentage_change: null,
    total_at_site_qty: row.currentInventory,
    total_targets: row.estimatedInventory,
    start_date: row.startDate || null,
    end_date: row.endDate || null,
    preparation_date: row.preparationDate,
    recommendation_date: null,
    recurrent_event_name: null,
    calculated_on: null,
    updated_at: row.lastModify,
    created_at: row.createdAt,
    promotion_ai_used: false,
    is_recalculating: false,
    calculation_errors: false,
    avg_sale_rate: row.avgSalesRate,
  }));
}
export async function getSpecialEventDetailsFromFlat(
  eventId: string,
): Promise<SpecialEventDetails | null> {
  const events = await getSpecialEventsFromFlat();
  const event = events.find((e) => e.id === eventId);
  if (!event) return null;
  const skuRows = await loadSkuLocationRows();
  const eventSkus = filterSkuRowsByEvent(skuRows, event.name);
  const locations = new Set(eventSkus.map((r) => r.locationId).filter(Boolean));
  const products = new Set(eventSkus.map((r) => r.productId).filter(Boolean));
  const skus = new Set(eventSkus.map((r) => r.skuId).filter(Boolean));
  const categories = new Set(eventSkus.map((r) => r.categories).filter(Boolean));
  return {
    id: event.id,
    name: event.name,
    external_id: event.external_id,
    description: event.description,
    avg_factor: event.avg_factor,
    recommendation_date: event.recommendation_date,
    total_involved_categories: categories.size,
    total_involved_stores: locations.size,
    total_involved_products: products.size,
    total_involved_skus: skus.size,
    total_targets: event.total_targets,
    preparation_date: event.preparation_date,
    start_date: event.start_date,
    end_date: event.end_date,
    stage: event.stage,
    promotion_ai_used: false,
    calculated_on: null,
    recalculation_scheduled_at: null,
    is_recalculating: false,
    calculation_errors: false,
  };
}
export async function getSpecialEventInventoriesFromFlat(
  eventId: string,
): Promise<SpecialEventInventory[]> {
  const events = await getSpecialEventsFromFlat();
  const event = events.find((e) => e.id === eventId);
  if (!event) return [];
  const skuRows = await loadSkuLocationRows();
  const eventSkus = filterSkuRowsByEvent(skuRows, event.name);
  return eventSkus.map((row, index) => ({
    id: `${eventId}-inv-${index + 1}`,
    inventory_id: `${eventId}-inv-${index + 1}`,
    promotion_ai_used: false,
    factor: row.factor,
    percentage_change: row.percentageChange,
    product_name: row.productName,
    product_external_id: row.productId,
    at_site: row.atSite,
    location_name: row.locationName,
    location_external_id: row.locationId,
    sku_name: row.skuName,
    sku_external_id: row.skuId,
    styles: row.styles ? [row.styles] : [],
    categories: row.categories ? [row.categories] : [],
    kind: row.kind ?? '',
    segment: row.segment ?? '',
    next_replenishment: row.nextReplenishment,
    preparation_date: row.preparationDate,
    optimal_stock: row.optimalStock,
    min_stock: row.minStock,
    max_stock: row.maxStock,
    edited: {},
    recommended_stock: row.optimalStock,
    is_recalculating: false,
    sale_rate: row.saleRate,
  }));
}
export async function getSpecialEventsAnalysisDashboardFromFlat(
  filters?: Record<string, unknown>,
): Promise<SpecialEventsAnalysisDashboardData> {
  const eventRows = await loadEventRows();
  let skuRows = await loadSkuLocationRows();
  const eventName = filters?.eventName as string | undefined;
  if (eventName) {
    skuRows = filterSkuRowsByEvent(skuRows, eventName);
  }
  const targetEvent = eventName
    ? eventRows.find((e) => matchesEventName(e.event, eventName))
    : null;
  const locationCoverage =
    targetEvent?.pctLocations ?? avg(eventRows.map((e) => e.pctLocations)) ?? 0;
  const skuCoverage = targetEvent?.pctSkus ?? avg(eventRows.map((e) => e.pctSkus)) ?? 0;
  const skuLocationCoverage =
    targetEvent?.pctSkuLocations ?? avg(eventRows.map((e) => e.pctSkuLocations)) ?? 0;
  const averageFactor = avg(skuRows.map((r) => r.factor)) ?? 0;
  const estimatedInventory = sum(skuRows.map((r) => r.optimalStock));
  const factorBuckets = new Map<number, number>();
  for (const row of skuRows) {
    if (row.factor == null) continue;
    const bucket = Math.round(row.factor * 10) / 10;
    factorBuckets.set(bucket, (factorBuckets.get(bucket) ?? 0) + 1);
  }
  const factorDistribution: SpecialEventsFactorDistributionItem[] = Array.from(
    factorBuckets.entries(),
  )
    .map(([factor, count]) => ({ factor, count }))
    .sort((a, b) => a.factor - b.factor);
  const locationGroups = groupBy(skuRows, (r) => r.locationName ?? 'Unknown');
  const locationAnalysis: SpecialEventsLocationAnalysisItem[] = Object.entries(locationGroups).map(
    ([loc, rows]) => {
      const factors = rows.map((r) => r.factor).filter((f): f is number => f != null);
      return {
        locationNameExternalId: loc,
        countInventory: rows.length,
        atPipe: sum(rows.map((r) => r.atSite)),
        avgFactor: avg(factors) ?? 0,
        minFactor: factors.length ? Math.min(...factors) : 0,
        maxFactor: factors.length ? Math.max(...factors) : 0,
        estimatedInventory: sum(rows.map((r) => r.optimalStock)),
        atWh: sum(rows.map((r) => r.atWh)),
        salesRate: avg(rows.map((r) => r.saleRate)) ?? 0,
      };
    },
  );
  const agGroups = groupBy(skuRows, (r) => r.assortmentGroup ?? 'Unknown');
  const assortmentGroupAnalysis: SpecialEventsAssortmentGroupAnalysisItem[] = Object.entries(
    agGroups,
  ).map(([ag, rows]) => {
    const factors = rows.map((r) => r.factor).filter((f): f is number => f != null);
    return {
      assortmentGroups: ag,
      skuLocation: rows.length,
      currentAtPipe: sum(rows.map((r) => r.atSite)),
      avgFactor: avg(factors) ?? 0,
      minFactor: factors.length ? Math.min(...factors) : 0,
      maxFactor: factors.length ? Math.max(...factors) : 0,
      estimatedInventory: sum(rows.map((r) => r.optimalStock)),
      atWh: sum(rows.map((r) => r.atWh)),
      salesRate: avg(rows.map((r) => r.saleRate)) ?? 0,
    };
  });
  const withFactor = skuRows
    .filter((r) => r.factor != null)
    .sort((a, b) => (b.factor ?? 0) - (a.factor ?? 0));
  const highFactors: SpecialEventsHighFactorItem[] = withFactor.slice(0, 10).map((r) => ({
    locationNameExternalId: r.locationName ?? '',
    skuNameExternalId: r.skuName ?? '',
    factor: r.factor ?? 0,
  }));
  const lowFactors: SpecialEventsLowFactorItem[] = withFactor
    .slice(-10)
    .reverse()
    .map((r) => ({
      locationNameExternalId: r.locationName ?? '',
      skuNameExternalId: r.skuName ?? '',
      factor: r.factor ?? 0,
    }));
  const skuLocationLevel: SpecialEventsSkuLocationLevelItem[] = skuRows.map((r) => ({
    locationNameExternalId: r.locationName ?? '',
    skuNameExternalId: r.skuName ?? '',
    productNameExternalId: r.productName ?? '',
    style: r.styles ?? '',
    color: '',
    departmentName: r.department ?? '',
    assortmentGroups: r.assortmentGroup ?? '',
    factor: r.factor ?? 0,
    optimalStock: r.optimalStock ?? 0,
    salesRate: r.saleRate ?? 0,
  }));
  return {
    locationCoverage,
    skuCoverage,
    skuLocationCoverage,
    averageFactor,
    estimatedInventory,
    factorDistribution,
    locationAnalysis,
    assortmentGroupAnalysis,
    highFactors,
    lowFactors,
    skuLocationLevel,
  };
}
export async function getSpecialEventsAnalysisFilterOptionsFromFlat(
  filterName: string,
): Promise<string[]> {
  const skuRows = await loadSkuLocationRows();
  const eventRows = await loadEventRows();
  const unique = (values: (string | null)[]) =>
    Array.from(new Set(values.filter((v): v is string => Boolean(v)))).sort();
  switch (filterName) {
    case 'name':
    case 'eventName':
      return unique(eventRows.map((r) => r.event));
    case 'loc_name_external_id':
    case 'location':
      return unique(skuRows.map((r) => r.locationName));
    case 'sku_name_external_id':
    case 'sku':
      return unique(skuRows.map((r) => r.skuName));
    case 'sku_product_name_external_id':
    case 'product':
      return unique(skuRows.map((r) => r.productName));
    case 'sku_department_name_external_id':
    case 'department':
      return unique(skuRows.map((r) => r.department));
    case 'sku_seasons':
    case 'season':
      return unique(skuRows.map((r) => r.season));
    case 'sku_brands':
    case 'brand':
      return unique(skuRows.map((r) => r.brand));
    case 'sku_assortment_group':
    case 'assortmentGroup':
      return unique(skuRows.map((r) => r.assortmentGroup));
    case 'sku_category':
    case 'category':
      return unique(skuRows.map((r) => r.categories));
    case 'sku_styles':
    case 'style':
      return unique(skuRows.map((r) => r.styles));
    case 'source_location_name':
    case 'sourceLocation':
      return unique(skuRows.map((r) => r.locationName));
    default:
      return [];
  }
}
export function clearSpecialEventsCache() {
  eventRowsCache = null;
  skuLocationRowsCache = null;
}
function sum(values: (number | null)[]): number {
  return values.reduce<number>((acc, v) => acc + (v ?? 0), 0);
}
function avg(values: (number | null)[]): number | null {
  const valid = values.filter((v): v is number => v != null);
  if (!valid.length) return null;
  return valid.reduce((acc, v) => acc + v, 0) / valid.length;
}
function groupBy<T>(items: T[], fn: (item: T) => string): Record<string, T[]> {
  const map: Record<string, T[]> = {};
  for (const item of items) {
    const key = fn(item);
    (map[key] ??= []).push(item);
  }
  return map;
}
